import { graphql } from 'react-relay';
import type { FC } from 'react';
import React, { useState, useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { styled } from '@compiled/react';

import Tabs, { TabList, Tab, useTabPanel } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { IN_EDITOR_TEMPLATES_LOAD_PANEL_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceFailure } from '@confluence/experience-tracker/entry-points/component.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { getSingleParam } from '@confluence/route-manager-utils/entry-points/getSingleParam.ts';
import type { ParsedUrlQuery } from '@confluence/route-manager-utils/entry-points/types.ts';
import { TemplatePanel } from '@confluence/template-panel';
import { SourcesList } from '@confluence/import-from-sources/entry-points/SourcesList.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { CloseButton } from '@confluence/editor-context-panel/entry-points/CloseButton.ts';
import { EditorContextPanelItem } from '@confluence/editor-context-panel/entry-points/EditorContextPanelItem.ts';
import { layersDialog, layersNavigation } from '@confluence/typography-placeholder';
import { SnippetPanel } from '@confluence/snippets/entry-points/SnippetPanel.ts';
import { expVal } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	CONTEXT_PANEL_TAB_TARGET,
	CONTEXT_PANEL_TAB_TARGET_BLOCK,
	CONTEXT_PANEL_TAB_TARGET_TEMPLATES,
	CONTEXT_PANEL_TAB_TARGET_IMPORT,
	PANEL_MODE_TEMPLATES,
	PANEL_MODE_IMPORT,
	type PanelMode,
} from '@confluence/template-utils/entry-points/urlUtils.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { SnippetsTabSpotlight } from '@confluence/snippets/entry-points/SnippetsTabSpotlight.ts';

import type {
	ContentCreationPanelTabsTemplatePanelContentQuery as ContentCreationPanelTabsTemplatePanelContentQuery$data,
	ContentCreationPanelTabsTemplatePanelContentQueryVariables as ContentCreationPanelTabsTemplatePanelContentQuery$variables,
} from './__types__/ContentCreationPanelTabsTemplatePanelContentQuery.ts';
export const i18n: {
	templatesTabLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	importDocTabLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	closeLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	closeLabelSnippets: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	blockLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	pageLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	templatesTabLabel: {
		id: 'editor-context-panel-content-creation-panel.template-panel.tabs.templates.label',
		defaultMessage: 'Templates',
		description:
			"The text is shown as the label for the 'Templates' tab in the content creation panel within the editor context panel (side panel). Selecting this tab displays available page templates.",
	},
	importDocTabLabel: {
		id: 'editor-context-panel-content-creation-panel.template-panel.tabs.import-doc.label',
		defaultMessage: 'Import',
		description:
			"The text is shown as the label for the 'Import' tab in the content creation panel within the editor context panel (side panel). Selecting this tab allows users to import a document from an external source.",
	},
	closeLabel: {
		id: 'editor-context-panel-content-creation-panel.close-label',
		defaultMessage: 'Close templates and import',
		description: 'Accessibility label for a cross icon that closes the in-editor context panel',
	},
	closeLabelSnippets: {
		id: 'editor-context-panel-content-creation-panel.close-label-snippets',
		defaultMessage: 'Close templates',
		description:
			'Accessibility label for a cross icon that closes the in-editor context panel when the snippets experiment is enabled',
	},
	blockLabel: {
		id: 'editor-context-panel-content-creation-panel.block-label',
		defaultMessage: 'Block',
		description:
			"The text is shown as the label for the 'Block' tab in the content creation panel within the editor context panel. Selecting this tab displays content creation options at the block (section) level.",
	},
	pageLabel: {
		id: 'editor-context-panel-content-creation-panel.page-label',
		defaultMessage: 'Page',
		description:
			"The text is shown as the label for the 'Page' tab in the content creation panel within the editor context panel. Selecting this tab displays content creation options at the full page level.",
	},
});

// Tab indices - the actual tab at each index varies by mode:
// - platform_editor_blocks_conf OFF (legacy): [Templates, Import]
// - platform_editor_blocks_conf ON + templates mode: [Page, Block]
// - platform_editor_blocks_conf ON + import mode: [Import] (only one tab)
const FIRST_TAB_INDEX = 0;
const SECOND_TAB_INDEX = 1;

type ContentWrapperProps = {
	disabled?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div<ContentWrapperProps>({
	backgroundColor: token('elevation.surface'),
	position: 'relative',
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: (props) => (props.disabled ? 'progress' : 'default'),
});

// header > body allows the sticky tabs to be on top of the tab body content as it scrolls
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StickyTabsHeaderWrapper = styled.div({
	position: 'sticky',
	top: token('space.negative.200'),
	paddingBottom: token('space.200'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: layersDialog,
	background: `linear-gradient(180deg, ${token('elevation.surface')} 85%, transparent 100%)`,
});

const closeIconWrapperStyles = xcss({
	height: '0px',
	position: 'absolute',
	right: '0',
	zIndex: 'dialog',
});
const CustomTabPanel: FC<{
	children?: React.ReactNode;
}> = ({ children }) => {
	const tabPanelAttributes = useTabPanel();
	return (
		<div
			{...tabPanelAttributes}
			style={{
				padding: `0 ${token('space.050')}`,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				zIndex: layersNavigation,
			}}
			tabIndex={-1}
		>
			{children}
		</div>
	);
};

const getParentPageId = (
	data: ContentCreationPanelTabsTemplatePanelContentQuery$data | undefined,
): string => {
	const contentAncestors = data?.content?.nodes?.[0]?.ancestors ?? [];
	return contentAncestors.slice(-1)[0]?.id || '';
};

// When platform_editor_blocks_conf is ON:
//   - 'import' target tab → opens import-only mode (single Import content)
//   - 'templates', 'block', or none target tab → opens templates mode (Page | Block tabs)
// When platform_editor_blocks_conf is OFF:
//   - Always templates mode (Templates | Import tabs)
const getPanelMode = (tabTarget: string | undefined, isImportDisabled?: boolean): PanelMode => {
	if (expVal('platform_editor_blocks_conf', 'isEnabled', false)) {
		if (tabTarget === CONTEXT_PANEL_TAB_TARGET_IMPORT && !isImportDisabled) {
			return PANEL_MODE_IMPORT;
		}
		return PANEL_MODE_TEMPLATES;
	}

	return PANEL_MODE_TEMPLATES;
};

// Gets the initial tab index based on mode and query params.
// When platform_editor_blocks_conf is ON in templates mode: page=0, block=1
// When platform_editor_blocks_conf is ON in import mode: import=0 (only one tab)
// When platform_editor_blocks_conf is OFF: templates=0, import=1
const getInitialTabTarget = (
	queryParams: ParsedUrlQuery,
	panelMode: PanelMode,
	isImportDisabled?: boolean,
): number => {
	const tabTarget = getSingleParam(queryParams, CONTEXT_PANEL_TAB_TARGET);

	if (expVal('platform_editor_blocks_conf', 'isEnabled', false)) {
		if (panelMode === PANEL_MODE_TEMPLATES && tabTarget === CONTEXT_PANEL_TAB_TARGET_BLOCK) {
			return SECOND_TAB_INDEX;
		}
		return FIRST_TAB_INDEX; // Default to first tab
	}

	// Legacy behavior: templates=0, import=1
	if (tabTarget === CONTEXT_PANEL_TAB_TARGET_IMPORT && !isImportDisabled) {
		return SECOND_TAB_INDEX;
	}
	return FIRST_TAB_INDEX;
};

type ContentCreationPanelTabsProps = {
	currentSpaceKey: string;
	contentId: string;
	spaceId: string;
	title: string;
	onContentUpdated: () => void;
	isPanelDisabled: boolean;
	beforeTemplateSelect?: () => void;
	beforeUpdateRequest?: () => void;
	onError?: () => void;
	closeConfigPanel: () => void;
	hasUserAddedContent: boolean | null;
	abortTemplatePanelLoadExperiences: (reason: string) => void;
	templateAllowList?: string[];
	isImportDisabled?: boolean;
	isAlwaysDiscardChanges?: boolean;
	isSpacePickerDisabled?: boolean;
};

export const ContentCreationPanelTabs: FC<ContentCreationPanelTabsProps> = ({
	currentSpaceKey,
	contentId,
	spaceId,
	title,
	onContentUpdated,
	isPanelDisabled,
	beforeTemplateSelect,
	hasUserAddedContent,
	beforeUpdateRequest,
	onError,
	closeConfigPanel,
	abortTemplatePanelLoadExperiences,
	templateAllowList,
	isImportDisabled,
	isAlwaysDiscardChanges,
	isSpacePickerDisabled,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isRovoLLMEnabled } = useSessionData();
	const { getQueryParams } = useContext(RoutesContext);
	// Derive panelMode from query params so that switching modes updates the tabs
	const queryParams = getQueryParams();
	const tabTarget = getSingleParam(queryParams, CONTEXT_PANEL_TAB_TARGET);
	const panelMode = useMemo(
		() => getPanelMode(tabTarget, isImportDisabled),
		[tabTarget, isImportDisabled],
	);
	const { setQueryParams } = useRouteActions();

	const [selectedTab, setSelectedTab] = useState(() =>
		getInitialTabTarget(getQueryParams(), panelMode, isImportDisabled),
	);

	// Update selected tab when panelMode or tabTarget changes
	const prevStateRef = useRef({ panelMode, tabTarget });
	useEffect(() => {
		// When neither platform_editor_blocks_conf nor cwr_blank_object_experiment is enabled, skip this effect
		// to preserve the initial tab selection and avoid race condition.
		if (
			!expVal('platform_editor_blocks_conf', 'isEnabled', false) &&
			(!isRovoLLMEnabled || !expVal('cwr_blank_object_experiment', 'isEnabled', false))
		) {
			return;
		}

		const prevState = prevStateRef.current;
		const hasPanelModeChanged = prevState.panelMode !== panelMode;
		const hasTabTargetChanged = prevState.tabTarget !== tabTarget;

		// Update tab when mode changes, or when tab target changes in templates mode
		if (hasPanelModeChanged || (hasTabTargetChanged && panelMode === PANEL_MODE_TEMPLATES)) {
			const newTab = getInitialTabTarget(getQueryParams(), panelMode, isImportDisabled);
			setSelectedTab(newTab);
		}

		prevStateRef.current = { panelMode, tabTarget };
	}, [panelMode, tabTarget, getQueryParams, isImportDisabled, isRovoLLMEnabled]);
	const { data, error, loading } = useQueryInterop<
		ContentCreationPanelTabsTemplatePanelContentQuery$data,
		ContentCreationPanelTabsTemplatePanelContentQuery$variables
	>(
		graphql`
			query ContentCreationPanelTabsTemplatePanelContentQuery($contentId: ID!) {
				content(id: $contentId, status: ["current", "draft"]) {
					nodes {
						id
						ancestors {
							id
							title
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId,
			},
			fetchPolicy: 'network-only',
		},
	);

	const parentPageId = getParentPageId(data);

	const handleTabSelect = useCallback(
		(selectedIndex: any) => {
			let actionSubjectId: string;
			if (expVal('platform_editor_blocks_conf', 'isEnabled', false)) {
				if (panelMode === PANEL_MODE_TEMPLATES) {
					// Templates mode: Page (0), Block (1)
					actionSubjectId = selectedIndex === SECOND_TAB_INDEX ? 'blockTab' : 'pageTab';

					const tabTargetQueryParam =
						selectedIndex === SECOND_TAB_INDEX
							? CONTEXT_PANEL_TAB_TARGET_BLOCK
							: CONTEXT_PANEL_TAB_TARGET_TEMPLATES;
					setQueryParams({ [CONTEXT_PANEL_TAB_TARGET]: tabTargetQueryParam }, true);
				} else {
					// Import mode: Import (0) - only one tab
					actionSubjectId = 'importTab';
				}
			} else {
				// Legacy: Templates (0), Import (1)
				actionSubjectId = selectedIndex === SECOND_TAB_INDEX ? 'importTab' : 'templatesTab';

				// When cwr_blank_object_experiment is enabled, we need to update the query param
				// to properly track which tab is selected (Templates vs Import)
				if (isRovoLLMEnabled && expVal('cwr_blank_object_experiment', 'isEnabled', false)) {
					const tabTargetQueryParam =
						selectedIndex === SECOND_TAB_INDEX
							? CONTEXT_PANEL_TAB_TARGET_IMPORT
							: CONTEXT_PANEL_TAB_TARGET_TEMPLATES;
					setQueryParams({ [CONTEXT_PANEL_TAB_TARGET]: tabTargetQueryParam }, true);
				}
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'editorRightRail',
					action: 'clicked',
					actionSubject: 'tab',
					actionSubjectId,
				},
			}).fire();
			setSelectedTab(selectedIndex);
		},
		[setSelectedTab, createAnalyticsEvent, panelMode, setQueryParams, isRovoLLMEnabled],
	);

	useEffect(() => {
		if (selectedTab !== FIRST_TAB_INDEX) {
			abortTemplatePanelLoadExperiences('templates tab is not selected');
		}
	}, [selectedTab, abortTemplatePanelLoadExperiences]);
	useEffect(() => {
		// Clear contextPanelTabTarget when both experiments are disabled
		if (
			!expVal('platform_editor_blocks_conf', 'isEnabled', false) &&
			(!isRovoLLMEnabled || !expVal('cwr_blank_object_experiment', 'isEnabled', false))
		) {
			setQueryParams({ [CONTEXT_PANEL_TAB_TARGET]: null }, true);
		}
	}, [setQueryParams, isRovoLLMEnabled]);

	if (loading) return null;
	// if this tabs array gets more complex, we will need to refactor the TEMPLATES_TAB_INDEX and CONTEXT_PANEL_TAB_TARGET concept into something that isnt hardcoded here
	const tabs = (() => {
		if (expVal('platform_editor_blocks_conf', 'isEnabled', false)) {
			// When platform_editor_blocks_conf is ON, we have two distinct modes:
			// 1. Templates mode: Page | Block tabs
			// 2. Import mode: Import only (single content)
			if (panelMode === PANEL_MODE_IMPORT) {
				return [
					{
						label: i18n.importDocTabLabel,
						content: (
							<SourcesList
								hasUserAddedContent={hasUserAddedContent}
								contentId={contentId}
								parentPageId={parentPageId}
								currentSpace={currentSpaceKey}
							/>
						),
					},
				];
			}
			// Templates mode: Page | Block
			return [
				{
					label: i18n.pageLabel,
					content: (
						<TemplatePanel
							parentPageId={parentPageId}
							currentSpaceKey={currentSpaceKey}
							contentId={contentId}
							title={title}
							onContentUpdated={onContentUpdated}
							isPanelDisabled={isPanelDisabled}
							beforeTemplateSelect={beforeTemplateSelect}
							hasUserAddedContent={hasUserAddedContent}
							beforeUpdateRequest={beforeUpdateRequest}
							onError={onError}
							templateAllowList={templateAllowList}
							isAlwaysDiscardChanges={isAlwaysDiscardChanges}
							isSpacePickerDisabled={isSpacePickerDisabled}
						/>
					),
				},
				{
					label: i18n.blockLabel,
					content: (
						<SnippetPanel contentId={contentId} spaceId={spaceId} spaceKey={currentSpaceKey} />
					),
				},
			];
		}

		// When platform_editor_blocks_conf is OFF: Templates | Import (legacy behavior)
		return [
			{
				label: i18n.templatesTabLabel,
				content: (
					<TemplatePanel
						parentPageId={parentPageId}
						currentSpaceKey={currentSpaceKey}
						contentId={contentId}
						title={title}
						onContentUpdated={onContentUpdated}
						isPanelDisabled={isPanelDisabled}
						beforeTemplateSelect={beforeTemplateSelect}
						hasUserAddedContent={hasUserAddedContent}
						beforeUpdateRequest={beforeUpdateRequest}
						onError={onError}
						templateAllowList={templateAllowList}
						isAlwaysDiscardChanges={isAlwaysDiscardChanges}
						isSpacePickerDisabled={isSpacePickerDisabled}
					/>
				),
			},
			...(!isImportDisabled
				? [
						{
							label: i18n.importDocTabLabel,
							content: (
								<SourcesList
									hasUserAddedContent={hasUserAddedContent}
									contentId={contentId}
									parentPageId={parentPageId}
									currentSpace={currentSpaceKey}
								/>
							),
						},
					]
				: []),
		];
	})();

	const panelWidth =
		expVal('platform_editor_blocks_conf', 'isEnabled', false) ||
		expVal('confluence_cwr_template_gallery', 'isEnabled', false)
			? 400
			: undefined;

	return (
		<EditorContextPanelItem
			itemName="template-panel"
			closeSelf={closeConfigPanel}
			customWidth={panelWidth}
		>
			<ErrorBoundary attribution={Attribution.PAGE_MODES}>
				{error && (
					<ErrorDisplay error={error}>
						<ExperienceFailure name={IN_EDITOR_TEMPLATES_LOAD_PANEL_EXPERIENCE} error={error} />
					</ErrorDisplay>
				)}
				<ContentWrapper disabled={isPanelDisabled} data-testid="content-creation-panel">
					<Tabs
						onChange={handleTabSelect}
						selected={selectedTab}
						id="editorContextPanelContentCreationTabs"
					>
						<StickyTabsHeaderWrapper>
							<Box xcss={closeIconWrapperStyles}>
								<CloseButton
									onClose={closeConfigPanel}
									label={intl.formatMessage(
										expVal('platform_editor_blocks_conf', 'isEnabled', false)
											? i18n.closeLabelSnippets
											: i18n.closeLabel,
									)}
								/>
							</Box>
							<TabList>
								{tabs.map((tab, index) => {
									const tabElement = (
										<Tab key={index}>
											<FormattedMessage {...tab.label} />
										</Tab>
									);

									// Wrap the Block tab (second tab) with the spotlight
									if (
										panelMode === PANEL_MODE_TEMPLATES &&
										index === SECOND_TAB_INDEX &&
										expVal('platform_editor_blocks_conf', 'isEnabled', false)
									) {
										return <SnippetsTabSpotlight key={index}>{tabElement}</SnippetsTabSpotlight>;
									}

									return tabElement;
								})}
							</TabList>
						</StickyTabsHeaderWrapper>
						{tabs.map((tab, index) => (
							<CustomTabPanel key={index}>{tab.content}</CustomTabPanel>
						))}
					</Tabs>
				</ContentWrapper>
			</ErrorBoundary>
		</EditorContextPanelItem>
	);
};
